import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { DataObject } from '@shared/models/data-object.model';
import { StepperComponent } from '@shared/components/stepper/stepper.component';
import { EntityType } from '@shared/models/entity-enum';

import { CompareAssociatedFieldsService } from './compare-associated-fields.service';

@Component({
  selector: 'miq-compare-associated-records',
  templateUrl: './compare-associated-records.component.html',
  styleUrls: ['./compare-associated-records.component.scss'],
  providers: [CompareAssociatedFieldsService],
  standalone: false,
})
export class CompareAssociatedRecordsComponent implements OnDestroy {
  public errorOccurred: boolean;
  public prevButtonDisabled: boolean;
  public nextButtonDisabled: boolean;
  public commonRowData: DataObject;
  public perRecordData: DataObject;
  public totalRecords: number;
  public currentIndex: number;
  public spaceSizeChange: string;
  public leaseCompId: number;
  public showSkipAndHideClose: boolean;
  public currentFlowData: DataObject;
  public count: number;
  private totalNumberOfFlows: number;
  private currentNumberFlow: number;
  private userSelections: boolean[] = [];

  private updatedValues: any = {};

  @ViewChild('stepper', { static: true }) stepper: StepperComponent;

  constructor(
    @Inject(CbreLoggerToken) private logger: CbreLogger,
    @Inject(MAT_DIALOG_DATA) private data: any,
    public dialogRef: MatDialogRef<CompareAssociatedRecordsComponent>,
    private compareAssociatedFieldsService: CompareAssociatedFieldsService
  ) {
    this.showSkipAndHideClose = Array.isArray(this.data) && this.data.length > 0;
    if (this.showSkipAndHideClose) {
      this.assignNextFlowValues();
      this.totalNumberOfFlows = this.data.length;
      this.currentNumberFlow = 1;
    } else {
      this.data = [this.data];
      this.assignNextFlowValues();
      this.totalNumberOfFlows = 1;
      this.currentNumberFlow = 1;
    }
  }

  ngOnDestroy() {
    // Required for translation
  }

  updateOrSkipClicked(userSelection: boolean) {
    if (!this.showSkipAndHideClose) {
      this.updateAssociatedRecord();
      return;
    }
    this.userSelections.push(userSelection);
    if (this.currentNumberFlow === this.totalNumberOfFlows) {
      if (this.userSelections.some(selection => !!selection)) {
        this.updateAssociatedRecord();
      } else {
        this.dialogRef.close();
      }
      return;
    }
    this.assignNextFlowValues(this.currentNumberFlow);
    this.currentNumberFlow = this.currentNumberFlow + 1;
  }

  assignNextFlowValues(index = 0): void {
    this.currentFlowData = this.data[index];
    if (this.currentFlowData.extraDetails && this.currentFlowData.extraDetails.spaceSizeChange) {
      const cantUpdateSpaceSize =
        this.currentFlowData.extraDetails.spaceSizeChange.entity === EntityType.LeaseComp
          ? 'recordsSearch.cantUpdateSpaceSizeInTenancy'
          : 'recordsSearch.cantUpdateSpaceSizeInLC';
      this.spaceSizeChange = this.currentFlowData.extraDetails.spaceSizeChange.hasSpaceSizeChanged
        ? cantUpdateSpaceSize
        : null;
    }
    if (this.currentFlowData.type === 'multi') {
      this.commonRowData = this.currentFlowData.commonRowData[0];
      this.perRecordData = this.currentFlowData.perRecordData[0];
      this.currentIndex = 1;
      this.totalRecords = this.currentFlowData.commonRowData.length;
      this.count = this.currentFlowData.commonRowData.length || this.currentFlowData.count;
      this.prevButtonDisabled = this.currentIndex === 1;
      this.nextButtonDisabled = this.currentIndex === this.totalRecords;
    } else {
      this.commonRowData = this.currentFlowData.commonRowData;
      this.leaseCompId = this.currentFlowData.extraDetails?.leaseRecord?.entity.id;
      this.perRecordData = this.currentFlowData.perRecordData;
      this.count = 1;
    }
  }

  public updateAssociatedRecord() {
    const digestedData = this.currentFlowData.digestedData(this.data, this.updatedValues, this.userSelections);
    this.stepper.next();
    this.compareAssociatedFieldsService
      .updateAssociatedRecordsFrom(this.currentFlowData.serviceCall, digestedData)
      .subscribe(
        res => {
          if (!!res) {
            this.errorOccurred = false;
          }
          this.stepper.next();
        },
        err => {
          this.logger.error(err);
          this.errorOccurred = true;
          this.stepper.next();
        }
      );
  }

  public swipe(action: string) {
    this.currentIndex = action === 'left' ? this.currentIndex - 1 : this.currentIndex + 1;
    this.commonRowData = this.data.commonRowData[this.currentIndex - 1];
    this.perRecordData = this.data.perRecordData[this.currentIndex - 1];
    this.prevButtonDisabled = this.currentIndex === 1;
    this.nextButtonDisabled = this.currentIndex === this.totalRecords;
  }

  public updateValue(changeEvent) {
    if (this.data.type === 'multi') {
      this.updatedValues[this.currentIndex] = this.updatedValues[this.currentIndex] || {};
      this.updatedValues[this.currentIndex][changeEvent.column.field] = changeEvent.value;
    } else {
      this.updatedValues[changeEvent.column.field] = changeEvent.value;
    }
  }
}
